/**
 * `@/db/client` do NAVEGADOR (ADR 0178): o mesmo contrato que o celular cumpre com o
 * op-sqlite, aqui cumprido por um SQLite em WebAssembly (sql.js) em memória.
 *
 * - `execute` devolve `{ rows, rowsAffected }` como o op-sqlite sempre devolveu.
 * - `db` é o drizzle via `sqlite-proxy`, o MESMO caminho do `test/dbHarness.ts` — provado
 *   por 89 suítes de repositório.
 * - `runInTransaction` vem da fábrica de `db/sqlPort.ts` (mutex FIFO + BEGIN/COMMIT).
 * - `initDatabase` roda o boot REAL (`db/bootstrap.ts`) com as migrações do app.
 *
 * Persistência fica para a Fase 1 (cópia em IndexedDB); por ora o banco nasce vazio a cada
 * abertura e é preenchido por restore/sync.
 */
import initSqlJs, { type Database, type SqlJsStatic, type Statement } from 'sql.js';
import sqlWasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import { drizzle } from 'drizzle-orm/sqlite-proxy';

import * as schema from '@/db/schema';
import { bootstrapDatabase } from '@/db/bootstrap';
import { createRunInTransaction, type SqlExecute, type SqlParam } from '@/db/sqlPort';
import migrations from '@/drizzle/migrations';
import { saveBytes } from './persist';

export type { RunInTransactionOptions, SqlExecute, SqlParam, SqlResult } from '@/db/sqlPort';

let SQL: SqlJsStatic | null = null;
let sqlite: Database | null = null;

function active(): Database {
  if (!sqlite) throw new Error('Kontto Web: banco ainda não aberto — chame openDatabase() antes');
  return sqlite;
}

const isReading = (sql: string) => /^\s*(select|pragma|with)/i.test(sql) || /\breturning\b/i.test(sql);

// Persistência: toda escrita agenda uma cópia do banco no IndexedDB (debounce). Barato
// (export de ~2 k linhas leva milissegundos) e simples; a Fase 2 pode trocar por OPFS.
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saving: Promise<void> = Promise.resolve();
export function scheduleSave(): void {
  // Conta a partir da PRIMEIRA escrita pendente (não reinicia a cada escrita): uma rajada de
  // escritas seguida de um F5 perdia tudo quando o timer era empurrado pra frente toda vez.
  if (saveTimer) return;
  saveTimer = setTimeout(() => { saveTimer = null; void flushSave(); }, 800);
}
// Fechar/recarregar a aba: tenta gravar o que ficou pendente (best-effort; o export é síncrono).
if (typeof window !== 'undefined') window.addEventListener('pagehide', () => { void flushSave(); });
export function flushSave(): Promise<void> {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  if (!sqlite) return saving;
  if (txDepth > 0) { saveTimer = setTimeout(() => { saveTimer = null; void flushSave(); }, 200); return saving; }
  const bytes = exportDatabase();
  saving = saving.then(() => saveBytes(bytes)).catch((e) => console.warn('[kontto-web] falha ao salvar o banco:', e));
  return saving;
}

const bind = (params?: SqlParam[]) => (params ?? []).map((p) => (p instanceof ArrayBuffer ? new Uint8Array(p) : p)) as never[];

// Escrita com parâmetros reaproveita o statement preparado (LRU por texto do SQL): o apply do sync
// repete o mesmo INSERT milhares de vezes e o `run` do sql.js recompilava a cada chamada. Sem
// parâmetros segue no `run`, que aceita vários statements num texto só (migrações, triggers).
// O `export()` do sql.js fecha todos os statements do banco: quem exporta zera o cache antes.
const STMT_CACHE_MAX = 64;
const stmtCache = new Map<string, Statement>();
function runWrite(database: Database, sql: string, params?: SqlParam[]): void {
  if (!params?.length) { database.run(sql); return; }
  let stmt = stmtCache.get(sql);
  if (stmt) { stmtCache.delete(sql); stmtCache.set(sql, stmt); }
  else {
    stmt = database.prepare(sql);
    stmtCache.set(sql, stmt);
    if (stmtCache.size > STMT_CACHE_MAX) {
      const oldest = stmtCache.keys().next().value as string;
      stmtCache.get(oldest)?.free();
      stmtCache.delete(oldest);
    }
  }
  stmt.run(bind(params)); // bind + step + reset
}
function dropStmtCache(free: boolean): void {
  if (free) for (const stmt of stmtCache.values()) { try { stmt.free(); } catch { /* já fechado */ } }
  stmtCache.clear();
}
// Transação em voo (BEGIN sem COMMIT/ROLLBACK): o export fecha e reabre a conexão, o que perderia
// a transação — o save agendado espera ela terminar.
let txDepth = 0;
function trackTx(sql: string): void {
  const head = sql.trimStart().slice(0, 12).toUpperCase();
  if (head.startsWith('BEGIN')) txDepth += 1;
  else if (head.startsWith('COMMIT') || head.startsWith('ROLLBACK')) txDepth = Math.max(0, txDepth - 1);
}

export const execute: SqlExecute = async (sql, params) => {
  const database = active();
  if (isReading(sql)) {
    const stmt = database.prepare(sql);
    try {
      stmt.bind(bind(params));
      const rows: Record<string, unknown>[] = [];
      while (stmt.step()) rows.push(stmt.getAsObject() as Record<string, unknown>);
      return { rows, rowsAffected: 0 };
    } finally {
      stmt.free();
    }
  }
  trackTx(sql);
  runWrite(database, sql, params);
  scheduleSave();
  return { rows: [], rowsAffected: database.getRowsModified() };
};

export const runInTransaction = createRunInTransaction(execute);

export const db = drizzle(
  async (sql, params, method) => {
    const database = active();
    if (method === 'run' && !isReading(sql)) {
      trackTx(sql);
      runWrite(database, sql, params as SqlParam[]);
      scheduleSave();
      // `rowsAffected` não está no tipo do sqlite-proxy, mas o `affected()` do backup lê se existir.
      return { rows: [], rowsAffected: database.getRowsModified() } as { rows: unknown[] };
    }
    const stmt = database.prepare(sql);
    try {
      stmt.bind(bind(params as SqlParam[]));
      const rows: unknown[][] = [];
      while (stmt.step()) rows.push(stmt.get() as unknown[]);
      // `get` devolve a LINHA (não a lista) — igual ao test/nodeSqlite.ts.
      return { rows: method === 'get' ? (rows[0] ?? []) : rows };
    } finally {
      stmt.free();
    }
  },
  { schema },
);

/** Abre um banco novo em memória (ou a partir de bytes exportados antes) e roda o boot real. */
export async function openDatabase(bytes?: Uint8Array): Promise<void> {
  SQL ??= await initSqlJs({ locateFile: () => sqlWasmUrl });
  dropStmtCache(false);
  txDepth = 0;
  sqlite?.close();
  sqlite = bytes ? new SQL.Database(bytes) : new SQL.Database();
  sqlite.run('PRAGMA foreign_keys = ON;');
  await initDatabase();
}

export async function initDatabase(): Promise<void> {
  await bootstrapDatabase({ execute, runInTransaction }, migrations);
}

/** Fecha o banco (descarta a cópia em memória; a persistida fica como está). */
export function closeDatabase(): void {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  dropStmtCache(false);
  txDepth = 0;
  sqlite?.close();
  sqlite = null;
}

/** Bytes do banco inteiro (formato de arquivo SQLite) — base da persistência em IndexedDB.
 * O sql.js fecha e reabre a conexão pra exportar: os statements preparados morrem (cache zerado
 * antes) e os PRAGMAs por conexão voltam ao padrão — o de chave estrangeira é religado aqui. */
export function exportDatabase(): Uint8Array {
  const database = active();
  dropStmtCache(false);
  const bytes = database.export();
  database.run('PRAGMA foreign_keys = ON;');
  return bytes;
}
